import React from 'react';
import HealthData from './HealthData';
import BabyHealthDataFilterForm from './Filter/BabyHealthDataFilterForm';
import useTranslation from '../../../Hooks/useTranslation';
import { QuestionnairesResultsReStructureScheme } from '../../Questionnaire/interfaces';

interface Props {
	patientId: string;
}

const BabyHealthData: React.FC<Props> = props => {
	const { t } = useTranslation();

	const questionnairesResultsStructure: QuestionnairesResultsReStructureScheme[] =
		[
			{
				title: t('component.monitoring.baby.vaginalBleeding') ?? '',
				key: 'meddiBabyLatamVaginalBleeding',
				contains: ['vaginalBleeding'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.temperature') ?? '',
				key: 'meddiBabyLatamTemperature',
				contains: ['temperature'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.signsOfUrinaryInfection') ?? '',
				key: 'meddiBabyLatamUrinaryTractInfection',
				contains: ['urinaryTractInfection'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.signsOfVaginalInfection') ?? '',
				key: 'meddiBabyLatamVaginalInfection',
				contains: ['vaginalInfection'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.vomiting') ?? '',
				key: 'meddiBabyLatamVomiting',
				contains: ['vomiting'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.diarrhealPictures') ?? '',
				key: 'meddiBabyLatamDiarrhoea',
				contains: ['diarrhoea'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.edema') ?? '',
				key: 'meddiBabyLatamSwelling',
				contains: ['swelling'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.edemaZone') ?? '',
				key: 'meddiBabyLatamSwelling',
				contains: ['swellingSpecify'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.medicationReaction') ?? '',
				key: 'meddiBabyLatamMedicaments',
				contains: ['medicaments'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.amnioticFluidLeak') ?? '',
				key: 'meddiBabyLatamAmnioticFluidLeak',
				contains: ['amnioticFluid'],
				filterSensitive: true,
			},
			{
				title:
					t('component.monitoring.baby.hypertensionSymptoms.headache') ?? '',
				key: 'meddiBabyLatamHypertensionSymptoms',
				contains: ['hypertensionSymptoms.headache'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.hypertensionSymptoms.vision') ?? '',
				key: 'meddiBabyLatamHypertensionSymptoms',
				contains: ['hypertensionSymptoms.vision'],
				filterSensitive: true,
			},
			{
				title:
					t('component.monitoring.baby.hypertensionSymptoms.hearing') ?? '',
				key: 'meddiBabyLatamHypertensionSymptoms',
				contains: ['hypertensionSymptoms.hearing'],
				filterSensitive: true,
			},
			{
				title:
					t('component.monitoring.baby.hypertensionSymptoms.stomachache') ?? '',
				key: 'meddiBabyLatamHypertensionSymptoms',
				contains: ['hypertensionSymptoms.stomachache'],
				filterSensitive: true,
			},
			{
				title:
					t('component.monitoring.baby.hypertensionSymptoms.breathing') ?? '',
				key: 'meddiBabyLatamHypertensionSymptoms',
				contains: ['hypertensionSymptoms.breathing'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.fetalMovements') ?? '',
				key: 'meddiBabyLatamFetalMovements',
				contains: ['fetalMovements'],
				filterSensitive: true,
			},
			{
				title:
					t('component.monitoring.baby.newborn.breastFeedingProblems') ?? '',
				key: 'meddiBabyLatamBreastSelfAssessment',
				contains: ['breastSelfAssessment'],
				filterSensitive: true,
			},
			{
				title: '¿Se percibe mucho ruido cuando el bebé succiona?' ?? '',
				key: 'meddiBabyLatamBreastSelfAssessment',
				contains: ['breastSelfAssessmentNoise'],
				filterSensitive: true,
			},
			{
				title:
					'¿Ha presentado alguna lesión en las mamas, desde el último chequeo?',
				key: 'meddiBabyLatamBreastSelfAssessment',
				contains: ['breastSelfAssessmentInjury'],
				filterSensitive: true,
			},
			{
				title: '¿Qué color tienen los loquios luego de los 5 días del parto?',
				key: 'meddiBabyLatamLochia',
				contains: ['lochia'],
				filterSensitive: false,
			},
			{
				title: '¿Los loquios presentan mal olor?',
				key: 'meddiBabyLatamLochia',
				contains: ['lochiaSmell'],
				filterSensitive: false,
			},
			{
				title: '¿Presenta dolor en la parte baja del abdomen?',
				key: 'meddiBabyLatamLowerAbdomenPain',
				contains: ['lowerAbdomenPain'],
				filterSensitive: false,
			},
			{
				title: 'Vía del parto',
				key: 'meddiBabyLatamRouteOfDelivery',
				contains: ['routeOfDelivery'],
				filterSensitive: true,
			},
			{
				title:
					'¿La herida operatoria tiene signos de enrojecimiento, aumento de temperatura o hinchazón?',
				key: 'meddiBabyLatamRouteOfDelivery',
				contains: ['routeOfDeliverySurgicalWound'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.uterineContractions') ?? '',
				key: 'meddiBabyLatamUterineContractions',
				contains: ['uterineContractions'],
				filterSensitive: true,
			},
			{
				title: t('component.monitoring.baby.glycemia.text') ?? '',
				key: 'meddiBabyLatamGlycemia',
				contains: ['glycemia'],
				unit: 'mg/dL',
				filterSensitive: true,
			},
		];

	return (
		<HealthData
			{...props}
			FilterComponent={BabyHealthDataFilterForm}
			questionnairesResultsStructure={questionnairesResultsStructure}
		/>
	);
};

export default BabyHealthData;
